import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../Logo';
import {
  ChartBar,
  SignOut,
  Robot,
  Image,
  LinkBreak,
  Users,
  ChartLineUp,
  Question,
  Gear,
  MagnifyingGlass,
  ChatCircleText,
  FilePdf,
  X,
  Sparkle,
} from '@phosphor-icons/react';

const navItems = [
  { path: '/', label: 'Dashboard', icon: ChartBar },
  { path: '/autoguard', label: 'AutoGuard', icon: Robot },
  { path: '/boost', label: 'AI Boost', icon: Sparkle },
  { path: '/cleanup', label: 'Innholdssjekk', icon: MagnifyingGlass },
  { path: '/insights', label: 'SEO', icon: ChartLineUp },
  { path: '/media', label: 'Media', icon: Image },
  { path: '/links', label: 'Lenker', icon: LinkBreak },
  { path: '/settings', label: 'Innstillinger', icon: Gear },
  { path: '/help', label: 'Hjelp & FAQ', icon: Question },
];

// Modules unlocked per shop_type — keeps Garnbutikk hidden for non-yarn tenants.
const SHOP_TYPE_NAV = {
  yarn: [{ path: '/garnbutikk', label: 'Garnbutikk', icon: FilePdf }],
};

const adminItems = [
  { path: '/admin/waitlist', label: 'Venteliste', icon: Users },
  { path: '/admin/tilly', label: 'Tilly innsikt', icon: ChatCircleText },
];

export default function Sidebar({ open = false, onClose }) {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`md:hidden fixed inset-0 bg-black/40 z-30 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        data-testid="sidebar-backdrop"
        aria-hidden={!open}
      />

      <aside
        className={`w-[240px] h-screen fixed left-0 top-0 bg-background border-r border-border flex flex-col z-40 transition-transform duration-200 ${
          open ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0`}
        data-testid="sidebar"
        aria-hidden={!open && typeof window !== 'undefined' && window.innerWidth < 768}
      >
        {/* Logo + mobile close */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <NavLink to="/" className="flex items-center gap-2" data-testid="sidebar-logo" onClick={onClose}>
            <Logo variant="full" height={28} />
          </NavLink>
          <button
            className="md:hidden p-1.5 rounded-md text-muted-foreground hover:bg-accent"
            onClick={onClose}
            aria-label="Lukk meny"
            data-testid="sidebar-close-btn"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {[...navItems, ...(SHOP_TYPE_NAV[user?.shop_type] || [])].map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));

            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Icon size={18} weight={isActive ? 'bold' : 'regular'} />
                {item.label}
              </NavLink>
            );
          })}

          {user?.role === 'admin' && (
            <>
              <div className="px-3 pt-4 pb-1 text-xs tracking-[0.15em] uppercase text-muted-foreground">Admin</div>
              {adminItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    data-testid={`nav-admin-${item.label.toLowerCase()}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <Icon size={18} weight={isActive ? 'bold' : 'regular'} />
                    {item.label}
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        {/* User */}
        <div className="p-3 border-t border-border">
          <div className="flex items-center justify-between px-3 py-2">
            <div className="truncate">
              <p className="text-sm font-medium truncate">{user?.name || user?.email}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
